import { useCallback, useEffect, useState } from 'react'

import { api, can, type ConnectorInstance, type ConnectorTemplate } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status, Toggletip, type Tone } from '../../components/kit'
import { Icon } from '../../components/ui'
import '../../styles/sweep.css'

// Email delivery, arranged the way the consent product arranges it.
//
// # WHY A PRESET THAT FILLS THREE FIELDS
//
// Because nobody remembers that Microsoft 365 is smtp.office365.com on 587
// with STARTTLS, and getting one of the three wrong produces a failure that
// reads like a wrong password. Choosing the provider fills all three, and
// typing over any of them moves the picker to Custom, so the picker never
// claims something the fields contradict.
//
// # WHY THE TEST IS A SEPARATE CARD
//
// There are two different questions and they fail differently. "Can this
// deployment reach the server and be let in" is answered without sending
// anything. "Does a message actually arrive" is answered only by a real
// message and a person with a mailbox open, because a relay that authenticates
// us and then drops everything not from its own domain passes the first and
// fails the second.
//
// # WHY THE EXPLANATIONS SIT BEHIND AN (i)
//
// Each field and card keeps one line that says what to do, and the reason for
// it opens beside the line. A settings page read once a year still has to be
// scanned, and a paragraph under every field hides the one that matters.

type Method = 'smtp' | 'microsoft_graph' | 'microsoft_oauth2' | 'resend' | 'ses'

const METHODS: { key: Method; label: string; what: string }[] = [
  {
    key: 'smtp',
    label: 'SMTP',
    what: 'Any SMTP server: Microsoft 365, Gmail, SES, SendGrid or your own relay',
  },
  {
    key: 'microsoft_graph',
    label: 'Microsoft 365 (Graph)',
    what: 'A directory application sending as your mailbox, needs Mail.Send, no SMTP ports',
  },
  {
    key: 'microsoft_oauth2',
    label: 'Microsoft 365 (SMTP with OAuth2)',
    what: "Microsoft's SMTP via a directory application, where Graph Mail.Send is refused",
  },
  {
    key: 'resend',
    label: 'Resend',
    what: 'Sends over HTTPS rather than SMTP, for a network where port 587 is closed',
  },
  {
    key: 'ses',
    label: 'Amazon SES',
    what: "This deployment's own permissions, from an address verified in that region",
  },
]

// The presets, and the same three values the consent product carries for each.
const PRESETS: { key: string; label: string; host: string; port: number; encryption: string }[] = [
  { key: 'custom', label: 'Custom', host: '', port: 587, encryption: 'starttls' },
  { key: 'microsoft365', label: 'Microsoft 365 / Outlook', host: 'smtp.office365.com', port: 587, encryption: 'starttls' },
  { key: 'gmail', label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 587, encryption: 'starttls' },
  { key: 'ses', label: 'Amazon SES', host: 'email-smtp.ap-south-1.amazonaws.com', port: 587, encryption: 'starttls' },
  { key: 'sendgrid', label: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, encryption: 'starttls' },
  { key: 'mailgun', label: 'Mailgun', host: 'smtp.mailgun.org', port: 587, encryption: 'starttls' },
  { key: 'postmark', label: 'Postmark', host: 'smtp.postmarkapp.com', port: 587, encryption: 'starttls' },
  { key: 'zoho', label: 'Zoho Mail', host: 'smtp.zoho.in', port: 587, encryption: 'starttls' },
]

const PORT_FOR: Record<string, number> = { starttls: 587, tls: 465, none: 25 }

// What the API sends back in place of a stored credential. Sending it back
// unchanged means "keep it", so the two ends have to agree on the exact
// string.
const MASK = '········'

type Form = Record<string, string>

export function EmailDelivery() {
  const [templates, setTemplates] = useState<Record<Method, ConnectorTemplate> | null>(null)
  const [instance, setInstance] = useState<ConnectorInstance | null>(null)
  const [method, setMethod] = useState<Method>('smtp')
  const [form, setForm] = useState<Form>({})
  const [preset, setPreset] = useState('custom')
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState('')
  const [issues, setIssues] = useState<string[]>([])
  const [error, setError] = useState<unknown>(null)

  const [testTo, setTestTo] = useState('')
  const [sendResult, setSendResult] = useState<{ sent: boolean; note: string; detail?: string; carrier?: string } | null>(null)
  const [probe, setProbe] = useState<{ allOk: boolean; note: string; checks: { check: string; ok: boolean; detail: string; unproven?: boolean }[] } | null>(null)

  const load = useCallback(() => {
    api
      .connectors()
      .then((v) => {
        const mail = v.groups.find((g) => g.category === 'MAIL')
        if (!mail) return
        const byKey = {} as Record<Method, ConnectorTemplate>
        let found: ConnectorInstance | null = null
        let foundKey: Method = 'smtp'
        for (const item of mail.items) {
          byKey[item.template.key as Method] = item.template
          if (item.instance) {
            found = item.instance
            foundKey = item.template.key as Method
          }
        }
        setTemplates(byKey)
        setInstance(found)
        setMethod(foundKey)
        const start: Form = {}
        const t = byKey[foundKey]
        for (const f of t?.fields ?? []) {
          const held = found?.config?.[f.key]
          start[f.key] = held !== undefined && held !== null ? String(held) : String(f.default ?? '')
        }
        setForm(start)
        setPreset(
          PRESETS.find((p) => p.key !== 'custom' && p.host === start.host && String(p.port) === String(start.port))?.key ??
            'custom',
        )
        setDirty(false)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  if (error != null) return <ErrorBox error={error} what="Email delivery" />
  if (!templates) return <Loading what="the mail configuration" />

  const mayWrite = can('config.write')
  const template = templates[method]

  function set(k: string, v: string) {
    setForm((old) => ({ ...old, [k]: v }))
    setDirty(true)
  }

  function pickMethod(m: Method) {
    setMethod(m)
    setDirty(true)
    const start: Form = {}
    for (const f of templates![m]?.fields ?? []) start[f.key] = String(f.default ?? '')
    setForm(start)
    setProbe(null)
    setSendResult(null)
  }

  function pickPreset(key: string) {
    setPreset(key)
    const p = PRESETS.find((x) => x.key === key)
    if (!p || p.key === 'custom') return
    setForm((old) => ({ ...old, host: p.host, port: String(p.port), encryption: p.encryption }))
    setDirty(true)
  }

  function pickEncryption(enc: string) {
    setForm((old) => ({ ...old, encryption: enc, port: String(PORT_FOR[enc] ?? 587) }))
    setDirty(true)
  }

  async function save() {
    setBusy('save')
    setIssues([])
    const body: Record<string, unknown> = {}
    for (const f of template?.fields ?? []) {
      const raw = (form[f.key] ?? '').trim()
      if (raw === '') continue
      body[f.key] = f.type === 'number' ? Number(raw) : raw
    }
    try {
      // Changing the method means a different connector, so the old one goes.
      // Two live mail connectors would leave which one sends to whichever the
      // worker happened to read first.
      if (instance && instance.templateKey !== method) {
        await api.removeConnector(instance.id)
        await api.addConnector(method, body)
      } else if (instance) {
        await api.saveConnector(instance.id, body)
      } else {
        await api.addConnector(method, body)
      }
      setDirty(false)
      setProbe(null)
      load()
    } catch (err) {
      const any = err as { issues?: { detail: string }[]; message?: string }
      setIssues(any.issues?.map((i) => i.detail) ?? [any.message ?? 'That was not saved.'])
    } finally {
      setBusy('')
    }
  }

  const field = (key: string) => template?.fields.find((f) => f.key === key)

  return (
    <div className="settings-stack">
      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>Email delivery</h2>
            <p className="obs-what">How an invitation, a reminder and a notice leave this deployment</p>
          </div>
          {instance && (
            <div className="std-meta">
              <InstanceStatus status={instance.status} />
            </div>
          )}
        </div>

        <div className="field-block">
          <span className="field-label">Delivery method</span>
          <div className="method-grid">
            {METHODS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={'method-card' + (method === m.key ? ' on' : '')}
                disabled={!mayWrite}
                onClick={() => pickMethod(m.key)}
              >
                <strong>{m.label}</strong>
                <span>{m.what}</span>
              </button>
            ))}
          </div>
        </div>

        {method === 'smtp' && (
          <>
            <div className="field-block">
              <label className="field-label" htmlFor="smtp-preset">
                Provider
              </label>
              <select id="smtp-preset" value={preset} onChange={(e) => pickPreset(e.target.value)} disabled={!mayWrite}>
                {PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
              <span className="field-note">Choosing one fills in the server, the port and the encryption</span>
            </div>

            <div className="field-row four">
              <label className="span-two">
                Server
                <input
                  value={form.host ?? ''}
                  onChange={(e) => {
                    set('host', e.target.value)
                    setPreset('custom')
                  }}
                  placeholder="smtp.example.com"
                  disabled={!mayWrite}
                />
              </label>
              <label>
                Port
                <input
                  type="number"
                  value={form.port ?? '587'}
                  onChange={(e) => set('port', e.target.value)}
                  disabled={!mayWrite}
                />
              </label>
              <label>
                Encryption
                <select value={form.encryption ?? 'starttls'} onChange={(e) => pickEncryption(e.target.value)} disabled={!mayWrite}>
                  <option value="starttls">STARTTLS (587)</option>
                  <option value="tls">TLS (465)</option>
                  <option value="none">None (25)</option>
                </select>
              </label>
            </div>

            <div className="field-row two">
              <label>
                Username
                <input
                  value={form.username ?? ''}
                  onChange={(e) => set('username', e.target.value)}
                  autoComplete="off"
                  disabled={!mayWrite}
                />
                <span className="field-note">Leave empty for a relay that authenticates nobody</span>
              </label>
              <SecretField
                label="Password"
                value={form.password ?? ''}
                onChange={(v) => set('password', v)}
                disabled={!mayWrite}
              />
            </div>

            <div className="field-row two">
              <label>
                Send as
                <input
                  type="email"
                  value={form.fromAddress ?? ''}
                  onChange={(e) => set('fromAddress', e.target.value)}
                  placeholder="risk@yourcompany.com"
                  disabled={!mayWrite}
                />
                <span className="field-note">{field('fromAddress')?.help}</span>
              </label>
              <label>
                Send as name
                <input value={form.fromName ?? ''} onChange={(e) => set('fromName', e.target.value)} disabled={!mayWrite} />
              </label>
            </div>
          </>
        )}

        {method === 'microsoft_graph' && (
          <>
            <div className="note">
              <strong>An application in Microsoft Entra ID, not a password</strong>
              Grant it Mail.Send on the mailbox below and paste its three values{' '}
              <Toggletip label="How to set up the application">
                Register an application in your directory, grant it Mail.Send on the mailbox below, and paste its three
                values here. This is what the FOCTTA consent product uses.
              </Toggletip>
            </div>
            <div className="field-row two">
              <label>
                Directory (tenant) id
                <input value={form.tenantId ?? ''} onChange={(e) => set('tenantId', e.target.value)} disabled={!mayWrite} />
              </label>
              <label>
                Application (client) id
                <input value={form.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} disabled={!mayWrite} />
              </label>
            </div>
            <div className="field-row two">
              <SecretField
                label="Client secret"
                value={form.clientSecret ?? ''}
                onChange={(v) => set('clientSecret', v)}
                disabled={!mayWrite}
              />
              <label>
                Send as
                <input
                  type="email"
                  value={form.sendAs ?? ''}
                  onChange={(e) => set('sendAs', e.target.value)}
                  placeholder="noreply@yourcompany.com"
                  disabled={!mayWrite}
                />
                <span className="field-note">The mailbox the application has been granted Mail.Send on</span>
              </label>
            </div>
            <div className="field-row two">
              <label>
                From name
                <input value={form.fromName ?? ''} onChange={(e) => set('fromName', e.target.value)} disabled={!mayWrite} />
                <span className="field-note">What a recipient sees, leave empty to use the deployment's</span>
              </label>
            </div>
          </>
        )}

        {method === 'microsoft_oauth2' && (
          <>
            <div className="note">
              <strong>Microsoft's own mail server, with a token instead of a password</strong>
              Uses an application in Entra ID granted SMTP.SendAsApp{' '}
              <Toggletip label="Why not a password">
                Microsoft 365 has switched off basic authentication for SMTP, so a username and password no longer work
                however correct they are. Its server, port and encryption are not asked for.
              </Toggletip>
            </div>
            <div className="field-row two">
              <label>
                Directory (tenant) id
                <input value={form.tenantId ?? ''} onChange={(e) => set('tenantId', e.target.value)} disabled={!mayWrite} />
              </label>
              <label>
                Application (client) id
                <input value={form.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)} disabled={!mayWrite} />
              </label>
            </div>
            <div className="field-row two">
              <SecretField
                label="Client secret"
                value={form.clientSecret ?? ''}
                onChange={(v) => set('clientSecret', v)}
                disabled={!mayWrite}
              />
              <label>
                Send as
                <input
                  type="email"
                  value={form.sendAs ?? ''}
                  onChange={(e) => set('sendAs', e.target.value)}
                  placeholder="noreply@yourcompany.com"
                  disabled={!mayWrite}
                />
                <span className="field-note">
                  The mailbox must also allow authenticated SMTP, off by default on new tenants
                </span>
              </label>
            </div>
            <div className="field-row two">
              <label>
                From name
                <input value={form.fromName ?? ''} onChange={(e) => set('fromName', e.target.value)} disabled={!mayWrite} />
                <span className="field-note">What a recipient sees, leave empty to use the deployment's</span>
              </label>
            </div>
          </>
        )}

        {method === 'resend' && (
          <>
            <div className="note">
              <strong>Over HTTPS, for a network where port 587 is closed</strong>
              Add your sending domain in Resend and verify it there first{' '}
              <Toggletip label="Why verify the domain first">
                Until it is verified Resend refuses every message from it, and the check below says so rather than
                letting you find out from a vendor.
              </Toggletip>
            </div>
            <div className="field-row two">
              <SecretField
                label="API key"
                value={form.apiKey ?? ''}
                onChange={(v) => set('apiKey', v)}
                disabled={!mayWrite}
              />
              <label>
                Send as
                <input
                  type="email"
                  value={form.fromAddress ?? ''}
                  onChange={(e) => set('fromAddress', e.target.value)}
                  placeholder="noreply@yourcompany.com"
                  disabled={!mayWrite}
                />
              </label>
            </div>
            <div className="field-row two">
              <label>
                From name
                <input value={form.fromName ?? ''} onChange={(e) => set('fromName', e.target.value)} disabled={!mayWrite} />
              </label>
            </div>
          </>
        )}

        {method === 'ses' && (
          <div className="field-row two">
            <label>
              Region
              <input value={form.region ?? 'ap-south-1'} onChange={(e) => set('region', e.target.value)} disabled={!mayWrite} />
            </label>
            <label>
              Send as
              <input
                type="email"
                value={form.fromAddress ?? ''}
                onChange={(e) => set('fromAddress', e.target.value)}
                disabled={!mayWrite}
              />
              <span className="field-note">A verified identity in that region</span>
            </label>
          </div>
        )}

        {issues.map((i) => (
          <p key={i} className="note warn">
            {i}
          </p>
        ))}

        {mayWrite && (
          <div className="p1-actions save-row">
            <button
              type="button"
              className="button primary"
              disabled={busy !== '' || !dirty}
              onClick={() => void save()}
              data-action="tprm.settings-email.a01"
              data-action-also="tprm.settings-email.a02 tprm.settings-email.a03 tprm.settings-email.a04"
            >
              {busy === 'save' ? 'Saving' : 'Save'}
            </button>
            {instance && (
              <button
                type="button"
                className="button"
                disabled={busy !== '' || dirty}
                onClick={async () => {
                  setBusy('probe')
                  try {
                    setProbe(await api.testConnector(instance.id))
                    load()
                  } finally {
                    setBusy('')
                  }
                }}
                data-action="tprm.settings-email.a05"
              >
                {busy === 'probe' ? 'Checking' : 'Check the connection'}
              </button>
            )}
            {dirty && (
              <span className="save-hint sw-row">
                <Status tone="warn">Unsaved</Status>
                Save before checking, or the check reads the old values
              </span>
            )}
          </div>
        )}

        {probe && <Checks result={probe} />}
        {!probe && instance?.lastError && <p className="note warn">{instance.lastError}</p>}
      </section>

      {/* The second question, and the only one a person can answer. */}
      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>Send a test message</h2>
            <p className="obs-what">Sends a real message, because reaching the server does not prove one arrives</p>
          </div>
        </div>

        {!instance ? (
          <div className="empty">
            <strong>Nothing is configured yet</strong>
            Save a delivery method above, then send a test
          </div>
        ) : (
          <>
            <div className="field-row send-row">
              <label>
                Send it to
                <input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="you@yourcompany.com"
                  disabled={!mayWrite}
                />
              </label>
              <button
                type="button"
                className="button primary"
                disabled={!mayWrite || testTo === '' || busy !== '' || dirty}
                onClick={async () => {
                  setBusy('send')
                  setSendResult(null)
                  try {
                    setSendResult(await api.sendTestEmail(instance.id, testTo))
                  } catch (err) {
                    const any = err as { message?: string }
                    setSendResult({ sent: false, note: 'Nothing was sent.', detail: any.message })
                  } finally {
                    setBusy('')
                  }
                }}
                data-action="tprm.settings-email.a06"
              >
                <Icon name="send" size={15} /> {busy === 'send' ? 'Sending' : 'Send'}
              </button>
            </div>
            {dirty && <p className="note warn">Save first, a test sends what is stored, not what is on screen</p>}
            {sendResult && (
              <div className={'note' + (sendResult.sent ? ' ok' : ' warn')} role="status">
                <p className="sw-row">
                  <Status tone={sendResult.sent ? 'ok' : 'crit'}>{sendResult.sent ? 'Sent' : 'Not sent'}</Status>
                  <strong>{sendResult.note}</strong>
                </p>
                {sendResult.detail && <p style={{ margin: '6px 0 0' }}>{sendResult.detail}</p>}
              </div>
            )}
          </>
        )}
      </section>

      <TeamMailboxes mayWrite={mayWrite} />
    </div>
  )
}

// The addresses this product writes TO, as opposed to the server it writes
// through. One tab, because they are two halves of the same question and a
// screen of its own for a single address was a tab somebody had to find.
function TeamMailboxes({ mayWrite }: { mayWrite: boolean }) {
  const [email, setEmail] = useState('')
  const [saved, setSaved] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .mailboxes()
      .then((m) => {
        setEmail(m.procurementEmail)
        setSaved(m.procurementEmail)
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [])

  return (
    <section className="panel">
      <div className="p1-head">
        <div>
          <h2>Where notices go</h2>
          <p className="obs-what">
            One address per purpose, not a copy of the staff list{' '}
            <Toggletip label="Why a team address">
              A person who leaves takes their own inbox with them; a team address does not.
            </Toggletip>
          </p>
        </div>
      </div>

      {error && <p className="note warn">{error}</p>}
      {note && <p className="note ok">{note}</p>}

      <div className="field-row send-row">
        <label>
          Procurement
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="procurement@yourcompany.example"
            disabled={!mayWrite}
            autoComplete="off"
          />
          <span className="field-note">
            Told when a vendor goes live and when it leaves{' '}
            <Toggletip label="Why those two moments">
              Those are the two moments that change what somebody may raise a purchase order against.
            </Toggletip>
          </span>
        </label>
        <button
          type="button"
          className="button"
          disabled={busy || !mayWrite || email === saved}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              const r = await api.setMailboxes(email)
              setSaved(r.procurementEmail)
              setNote(r.note)
            } catch (err) {
              const any = err as { issues?: { detail: string }[]; message?: string }
              setError(any.issues?.map((i) => i.detail).join(' ') || any.message || 'That was not saved.')
            } finally {
              setBusy(false)
            }
          }}
          data-action="tprm.settings-email.a07"
        >
          {busy ? 'Saving' : 'Save'}
        </button>
      </div>
    </section>
  )
}

// A secret is stored as where it lives. The field says so once, here, rather
// than in every schema, and shows the reference rather than hiding it: there is
// nothing to hide, which is the whole point.
function SecretField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <label>
      {label}
      <input
        type={value === MASK ? 'text' : 'password'}
        value={value}
        onFocus={() => {
          // The mask means "what is stored stays". Clearing it on focus is
          // what makes replacing a credential possible without making an
          // accidental click destroy one.
          if (value === MASK) onChange('')
        }}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste it here"
        autoComplete="off"
        disabled={disabled}
      />
      {/* A stored credential keeps its whole sentence on screen, because it
          tells somebody about to click what the click does. The rest is the
          reason, and waits behind the (i). */}
      <span className="field-note">
        {value === MASK ? (
          'A credential is stored, click here to replace it or leave it and it stays'
        ) : (
          <>
            Stored encrypted and never shown again{' '}
            <Toggletip label="How a credential is stored">
              Stored encrypted with this deployment's key and never shown again. An operator may give env:NAME or
              secret:NAME instead.
            </Toggletip>
          </>
        )}
      </span>
    </label>
  )
}

// The connector's own state, as a label. Not proved is neither working nor
// failing, so it gets its own quiet word rather than borrowing either.
function InstanceStatus({ status }: { status: ConnectorInstance['status'] }) {
  if (status === 'ACTIVE') return <Status tone="ok">Working</Status>
  if (status === 'FAILING') return <Status tone="warn">Not working</Status>
  if (status === 'PAUSED') return <Status tone="neutral">Paused</Status>
  return <Status tone="neutral">Not proved</Status>
}

// A check's answer is one of three, and unproven stays its own label: folding
// it into passed is how a screen claims mail works when nobody checked.
function checkLabel(c: { ok: boolean; unproven?: boolean }): { tone: Tone; word: string } {
  if (c.unproven) return { tone: 'warn', word: 'Unproven' }
  if (c.ok) return { tone: 'ok', word: 'Passed' }
  return { tone: 'crit', word: 'Refused' }
}

function Checks({ result }: { result: { allOk: boolean; note: string; checks: { check: string; ok: boolean; detail: string; unproven?: boolean }[] } }) {
  return (
    <div className={'note' + (result.allOk ? ' ok' : ' warn')} role="status">
      <strong>{result.note}</strong>
      <ul className="probe-list">
        {result.checks.map((c) => {
          const label = checkLabel(c)
          return (
            <li key={c.check} className={c.unproven ? 'probe-open' : c.ok ? 'probe-ok' : 'probe-no'}>
              <Status tone={label.tone}>{label.word}</Status>
              <span>
                <strong>{c.check}</strong> {c.detail}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
